import { Accordion, AccordionSummary, AccordionDetails } from '@rbx/ui';
import React, { FunctionComponent, useCallback } from 'react';
import { useTranslation } from '@rbx/intl';
import { useAppMainState } from '@modules/layout/layout/components/AppMainStateProvider';
import { MemberType } from '@modules/client';
import useAppMainConfig from '@modules/ui/shared/hooks/useAppMainConfig';
import CharacteristicsIterator, { CharacteristicsIteratorProps } from './CharacteristicsIterator';
import useCharacteristicsTableContainerStyles from './CharacteristicsTableContainer.styles';
import ViewInheritedFromLink, {
  InstanceReferenceMemberName,
  ObjectReferenceMemberName,
} from '../../sharedUI/components/ViewInheritedFromLink';
import {
  isInheritInstanceIteratorKey,
  isInheritObjectIteratorKey,
} from '../../utils/apiReferenceUtils';

interface CollapsibleCharacteristicsIteratorProps extends CharacteristicsIteratorProps {
  name: string;
  iteratorKey: string;
  attribute: string;
  memberType: MemberType;
}

const CollapsibleCharacteristicsIterator: FunctionComponent<
  React.PropsWithChildren<CollapsibleCharacteristicsIteratorProps>
> = ({ rows, name, attribute, iteratorKey, memberType }) => {
  const { translate } = useTranslation();
  const { accordionsExpanded, setAccordionExpanded } = useAppMainState();
  const { setAccordionExpanded: setAccordionExpandedInPageNav } = useAppMainConfig();
  const {
    classes: { accordionGrid, accordion, instanceLink },
  } = useCharacteristicsTableContainerStyles();

  const handleChange = useCallback(
    (_: React.ChangeEvent<object> | null, isExpanded: boolean) => {
      setAccordionExpanded(iteratorKey, isExpanded);
      setAccordionExpandedInPageNav(iteratorKey, isExpanded);
    },
    [iteratorKey, setAccordionExpanded, setAccordionExpandedInPageNav],
  );

  if (rows.length > 0) {
    if (isInheritInstanceIteratorKey(iteratorKey)) {
      return (
        <ViewInheritedFromLink
          className={instanceLink}
          memberType={memberType}
          engineReferenceClassName={InstanceReferenceMemberName}
        />
      );
    }
    if (isInheritObjectIteratorKey(iteratorKey)) {
      return (
        <ViewInheritedFromLink
          className={instanceLink}
          memberType={memberType}
          engineReferenceClassName={ObjectReferenceMemberName}
        />
      );
    }
    return (
      <div className={accordionGrid} id={iteratorKey}>
        <Accordion
          className={accordion}
          expanded={accordionsExpanded.includes(iteratorKey)}
          onChange={handleChange}
          TransitionProps={{
            mountOnEnter: true,
            timeout: 200,
          }}>
          <AccordionSummary>
            {translate('Label.AttributeInheritedFrom', { name, attribute }) ||
              `${attribute} inherited from ${name}`}
          </AccordionSummary>
          <AccordionDetails>
            <CharacteristicsIterator rows={rows} />
          </AccordionDetails>
        </Accordion>
      </div>
    );
  }

  return null;
};

export default CollapsibleCharacteristicsIterator;
